import { Theme } from '@desygner/ui-common-components';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { TailSpin } from 'react-loader-spinner';
import styled from 'styled-components';

import Store, { actions } from '@store';
import translate from '@translations';
import useDimensions from 'src/hooks/useDimensions';

import Header, { headerHeight } from './Header';
import { Actions, useViewport } from './ViewportProvider';
import VirtualPages from './VirtualPages';
import ZoomButtons from './ZoomButtons';
import { TextOverflowChainHandler } from 'assets/js/editor/factory/text-overflow/chainHandler';

const chainHandler = new TextOverflowChainHandler();

const StyledViewportWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const ViewportPageWrapper = styled.div`
  width: 100%;
  height: calc(100% - ${headerHeight}px);
  background-color: ${({ theme }) => theme.Color.Neutrals.Light};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  overflow-y: auto;
`;

const ZoomWrapper = styled.div`
  zoom: ${({ zoom }) => zoom * 100}%;
  width: 100%;
  height: 100%;
`;

const Loader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  pointer-events: none;
`;

const unlinkPrev = (element) => {
  const next = element;
  const prev = chainHandler.findTextLink(next.id, next.pageId)?.parent;

  chainHandler.addTextElementToTextMock(prev.id, prev.pageId);
  chainHandler.addTextElementToTextMock(next.id, next.pageId);

  Store.dispatch(actions.editor.removeTextLink({ next }));
};

const unlinkNext = (element) => {
  const prev = element;
  const next = chainHandler.findTextLink(prev.id, prev.pageId)?.child;

  chainHandler.addTextElementToTextMock(prev.id, prev.pageId);
  chainHandler.addTextElementToTextMock(next.id, next.pageId);

  Store.dispatch(actions.editor.removeTextLink({ prev }));
};

const addLink = (prev, next) => {
  Store.dispatch(actions.editor.addTextLink({ prev, next }));
};

const getElementAttributes = ({ target }) => {
  const elementContainer = target.closest('.element-container');
  if (!elementContainer) return {};

  return {
    id: elementContainer.dataset.id,
    pageId: Number(elementContainer.dataset.pageId)
  };
};

const ViewportWrapper = ({ onClose = () => { }, loading }) => {
  const { dispatch, state: { zoom } } = useViewport();
  const scrollRef = useRef();
  const viewportRef = useRef();
  const viewportDimensions = useDimensions(viewportRef);
  const [draggedElement, setDraggedElement] = useState(null);

  useEffect(() => {
    if (viewportDimensions.width) {
      dispatch({ type: Actions.set_viewport_page_width, payload: viewportDimensions.width });
    }
  }, [viewportDimensions.width]);

  useEffect(() => {
    const handleKeyDown = ({ key, metaKey, ctrlKey }) => {
      const shouldClose = key === 'Escape';
      const shouldZoomIn = (key === '+' || key === '=') && (metaKey || ctrlKey);
      const shouldZoomOut = (key === '_' || key === '-') && (metaKey || ctrlKey);

      if (shouldClose) return onClose();
      if (shouldZoomIn) return dispatch({ type: Actions.zoom_in });
      if (shouldZoomOut) return dispatch({ type: Actions.zoom_out });
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDragStart = (e) => {
    const attributes = getElementAttributes(e);

    if (!attributes.id) return;

    setDraggedElement(attributes);
  };

  const handleDragEnd = () => {
    setDraggedElement(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const attributes = getElementAttributes(e);

    if (!attributes.id) return;

    const prev = draggedElement;
    const next = attributes;

    addLink(prev, next);
    setDraggedElement(null);
  };

  const handleClick = (e) => {
    const attributes = getElementAttributes(e);
    const button = e.target.closest('div');

    if (!attributes.id || !button) return;

    if (button.classList.contains('prev-link-button')) {
      unlinkPrev(attributes);
    }

    if (button.classList.contains('next-link-button')) {
      unlinkNext(attributes);
    }
  };

  return (
    <StyledViewportWrapper ref={viewportRef}>
      <Header
        onClose={onClose}
        title={translate('text_overflow.viewport_title')}
        closeText={'Close'}
      />
      <ZoomButtons />
      <ViewportPageWrapper
        ref={scrollRef}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <ZoomWrapper zoom={zoom}>
          {loading && (
            <Loader>
              <TailSpin
                height="60"
                width="60"
                color={Theme.Color.System.Primary}
              />
            </Loader>
          )}
          <VirtualPages scrollRef={scrollRef} />
        </ZoomWrapper>
      </ViewportPageWrapper>
    </StyledViewportWrapper>
  );
};

ViewportWrapper.propTypes = {
  loading: PropTypes.bool,
  onClose: PropTypes.func
};

export default ViewportWrapper;
